import { useState } from 'react'
import { enqueueSnackbar } from 'notistack'

import { logSentry } from '@gto/sentry'
import { IconGtoWizardLogoSigned } from '@gto/ui/assets'
import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import type { LoginReason } from '@/pages/login/login-search'
import { loginCopyFor } from '@/pages/login/login-search'
import { buildOAuthRedirectUrl } from '@/utils/oauth'

type LoginExplainerProps = {
  /** Deep-link target to restore after sign-in. */
  from: string | undefined
  /** Why the reader is here; `undefined` for the ordinary not-signed-in case. */
  reason: LoginReason | undefined
}

/**
 * The `/login` screen. Takes its search values as props rather than reading the
 * file route, so it renders under a plain `render()` in tests — the route stays
 * the thin binding that owns `validateSearch`.
 */
export const LoginExplainer = ({ from, reason }: LoginExplainerProps) => {
  const [pending, setPending] = useState(false)
  const copy = loginCopyFor(reason)

  const handleOAuthSignIn = () => {
    setPending(true)
    buildOAuthRedirectUrl(from)
      .then((url) => {
        window.location.href = url
      })
      .catch((e: unknown) => {
        logSentry('admin-web: OAuth redirect URL build failed', e)
        enqueueSnackbar({ heading: 'Could not start sign-in', variant: 'error' })
        setPending(false)
      })
  }

  return (
    <div
      className="flex h-[100dvh] w-full items-center justify-center overflow-hidden p-6"
      data-tst="admin-login-explainer"
    >
      <div className="flex w-full max-w-[420px] flex-col gap-8 rounded-lg border border-divider bg-paper-elevation1 p-8 sm:p-6">
        <IconGtoWizardLogoSigned style={{ height: 24, width: 'auto' }} />

        <div className="flex flex-col gap-2">
          <Typography
            className="font-semibold"
            color="text.primary"
            // The reason is the whole point of the screen when there is one, so it
            // carries its own hook — a test asserting "the bounce is explained"
            // should not have to match on prose.
            data-tst={reason === undefined ? 'admin-login-heading' : 'admin-login-reason'}
            variant="h5"
          >
            {copy.heading}
          </Typography>
          <Typography
            color="text.secondary"
            variant="body2"
          >
            {copy.body}
          </Typography>
        </div>

        <Button
          fullWidth
          color="primary"
          data-tst="admin-login-cta"
          disabled={pending}
          variant="contained"
          onClick={handleOAuthSignIn}
        >
          {pending ? 'Redirecting…' : 'Go to GTO Wizard sign-in'}
        </Button>
      </div>
    </div>
  )
}
